import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API_URL } from '../config/api';
import {
  Mail, MapPin, ChevronRight, Phone
} from 'lucide-react';

function Footer() {
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${API_URL}/api/categories`);
        const type = res.headers.get('content-type') || '';
        if (res.ok && type.includes('application/json')) {
          const json = await res.json();
          if (json.success && Array.isArray(json.data) && json.data.length > 0) {
            if (isMounted) setCategories(json.data);
            return;
          }
        }

        const filterRes = await fetch(`${API_URL}/api/products/filters/options`);
        const filterType = filterRes.headers.get('content-type') || '';
        if (filterRes.ok && filterType.includes('application/json')) {
          const filterJson = await filterRes.json();
          if (filterJson.success && filterJson.filters?.categories && filterJson.filters.categories.length > 0) {
            const parsed = filterJson.filters.categories.map(c => ({ id: c, name: c }));
            if (isMounted) setCategories(parsed);
          }
        }
      } catch (err) {
        // Fallback maintained
      }
    };
    fetchCategories();
    return () => { isMounted = false; };
  }, []);

  return (
    <footer className="bg-slate-50 text-slate-900 border-t border-slate-200 relative overflow-hidden font-sans">
      
      {/* Soft Ambient Background Orbs */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-400/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-400/10 rounded-full blur-3xl pointer-events-none" />

      {/* ── LIGHT MULTI-COLUMN SITEMAP ── */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">

          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1 space-y-4">
            <div className="flex items-center gap-2.5">
              <img 
                src="/images/logoicon.png" 
                alt="BrightBazaar" 
                className="w-9 h-9 rounded-xl bg-white p-1 border border-orange-200 shadow-sm"
              />
              <span className="text-lg font-black italic tracking-tight text-slate-900 uppercase">
                Bright<span className="text-orange-600">bazaar</span>
              </span>
            </div>

            <p className="text-xs text-slate-600 leading-relaxed">
              India's next-generation e-commerce hub powered by BrightPlanet Ventures. Delivering top tech, home appliances &amp; lifestyle goods with 0% No-Cost EMI options.
            </p>

            <div className="space-y-1.5 pt-2 text-xs">
              <div className="flex items-center gap-2 text-slate-800">
                <Phone className="w-3.5 h-3.5 text-orange-600 shrink-0" />
                <span className="font-extrabold">+91 73532 97211</span>
              </div>
              <div className="flex items-center gap-2 text-slate-600">
                <Mail className="w-3.5 h-3.5 text-orange-600 shrink-0" />
                <span className="truncate text-[11px]">info@brightplanetventures.com</span>
              </div>
              <p className="text-[10px] text-slate-500 pt-1 font-mono">GSTIN: 29AAKCD7090A1ZL</p>
            </div>
          </div>

          {/* Column 2: Account */}
          <div>
            <h4 className="text-xs font-black uppercase tracking-widest text-slate-900 mb-4 border-b border-slate-200 pb-2">
              My Account
            </h4>
            <ul className="space-y-2.5 text-xs text-slate-600">
              <li><Link to="/account" className="hover:text-orange-600 flex items-center gap-1.5 transition-colors"><ChevronRight className="w-3 h-3 text-orange-600" />Dashboard</Link></li>
              <li><Link to="/orders" className="hover:text-orange-600 flex items-center gap-1.5 transition-colors"><ChevronRight className="w-3 h-3 text-orange-600" />My Orders</Link></li>
              <li><Link to="/wishlist" className="hover:text-orange-600 flex items-center gap-1.5 transition-colors"><ChevronRight className="w-3 h-3 text-orange-600" />Wishlist</Link></li>
              <li><Link to="/coupons" className="hover:text-orange-600 flex items-center gap-1.5 transition-colors"><ChevronRight className="w-3 h-3 text-orange-600" />Coupons &amp; Offers</Link></li>
            </ul>
          </div>

          {/* Column 3: Customer Policies */}
          <div>
            <h4 className="text-xs font-black uppercase tracking-widest text-slate-900 mb-4 border-b border-slate-200 pb-2">
              Policies &amp; Help
            </h4>
            <ul className="space-y-2.5 text-xs text-slate-600">
              <li><Link to="/return-policy" className="hover:text-orange-600 flex items-center gap-1.5 transition-colors"><ChevronRight className="w-3 h-3 text-orange-600" />Return Policy</Link></li>
              <li><Link to="/refund-policy" className="hover:text-orange-600 flex items-center gap-1.5 transition-colors"><ChevronRight className="w-3 h-3 text-orange-600" />Refund Policy</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-orange-600 flex items-center gap-1.5 transition-colors"><ChevronRight className="w-3 h-3 text-orange-600" />Privacy Policy</Link></li>
              <li><Link to="/disclaimer" className="hover:text-orange-600 flex items-center gap-1.5 transition-colors"><ChevronRight className="w-3 h-3 text-orange-600" />Disclaimer</Link></li>
            </ul>
          </div>

          {/* Column 4: Dynamic Database Categories */}
          <div>
            <h4 className="text-xs font-black uppercase tracking-widest text-slate-900 mb-4 border-b border-slate-200 pb-2">
              Hot Categories
            </h4>
            <ul className="space-y-2.5 text-xs text-slate-600">
              {(categories.length > 0 ? categories : [
                { name: 'Technology' },
                { name: 'Home Tech' },
                { name: 'Fashion' },
                { name: 'Lifestyle' }
              ]).map((cat) => {
                const name = cat.name || cat;
                return (
                  <li key={cat.id || name}>
                    <Link to={`/categories/${encodeURIComponent(name)}/products`} className="hover:text-orange-600 flex items-center gap-1.5 transition-colors">
                      <ChevronRight className="w-3 h-3 text-orange-600" />
                      {name}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Column 5: Store Hub Locations */}
          <div>
            <h4 className="text-xs font-black uppercase tracking-widest text-slate-900 mb-4 border-b border-slate-200 pb-2 flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5 text-orange-600" /> Regional Hubs
            </h4>
            <div className="space-y-3 text-xs">
              <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
                <p className="font-extrabold text-slate-900 text-[11px]">Bailhongal HQ</p>
                <p className="text-[10px] text-slate-500 leading-tight mt-0.5">Mulla Complex, Somwar Peth, Bailhongal - 591102</p>
              </div>
              <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
                <p className="font-extrabold text-slate-900 text-[11px]">Gokak Branch</p>
                <p className="text-[10px] text-slate-500 leading-tight mt-0.5">Navarang Plaza, Naka No.1 Gokak - 591307</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ── LIGHT BOTTOM TRUST & PAYMENT BADGES ── */}
      <div className="bg-slate-100 border-t border-slate-200 py-6">
        <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-600">
          <p>© 2026 BRIGHTBAZAAR / BrightPlanet Ventures. All Rights Reserved.</p>
          
          {/* Payment Methods */}
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-black uppercase tracking-wider text-slate-600">Accept Payment:</span>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-1 bg-white border border-slate-200 rounded text-[10px] font-black text-slate-900 shadow-sm">VISA</span>
              <span className="px-2.5 py-1 bg-white border border-slate-200 rounded text-[10px] font-black text-blue-700 shadow-sm">PAYPAL</span>
              <span className="px-2.5 py-1 bg-white border border-slate-200 rounded text-[10px] font-black text-orange-600 shadow-sm">MASTERCARD</span>
              <span className="px-2.5 py-1 bg-white border border-slate-200 rounded text-[10px] font-black text-blue-600 shadow-sm">UPI / QR</span>
              <span className="px-2.5 py-1 bg-white border border-slate-200 rounded text-[10px] font-black text-green-600 shadow-sm">COD</span>
            </div>
          </div>
        </div>
      </div>

    </footer>
  );
}

export default Footer;
